import React, { useState, useCallback } from 'react';
import { View, TextInput, TouchableOpacity, Text, StyleSheet } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout, AdminSelectDropdown } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThreadModeration } from '@/hooks/admin/useThreadModeration';
import { ThreadModerationList } from '@/components/admin/corkBoard/ThreadModerationList';
import { EditThreadTagsModal } from '@/components/admin/corkBoard/EditThreadTagsModal';
import { AnnouncementFormModal } from '@/components/admin/corkBoard/AnnouncementFormModal';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import { showConfirm } from '@/utils/common/alert';
import { DesktopThreadModerationView } from './desktop/DesktopThreadModerationView';
import type { CorkBoardThreadSummary, ThreadSortBy } from '@/types/residents/corkBoard';

type ThreadModerationScreenProps = StackScreenProps<AdminStackParamList, 'ThreadModeration'>;

export const ThreadModerationScreen: React.FC<ThreadModerationScreenProps> = ({ navigation }) => {
  const { layoutMode } = useResponsive();
  const moderation = useThreadModeration();

  const [editTagsVisible, setEditTagsVisible] = useState(false);
  const [editingThread, setEditingThread] = useState<CorkBoardThreadSummary | null>(null);
  const [announcementVisible, setAnnouncementVisible] = useState(false);

  const handleOpenEditTags = useCallback((thread: CorkBoardThreadSummary) => {
    setEditingThread(thread);
    setEditTagsVisible(true);
  }, []);

  const handleCloseEditTags = useCallback(() => {
    setEditTagsVisible(false);
    setEditingThread(null);
  }, []);

  const handleSaveTags = useCallback(
    async (threadId: string, tagIds: number[]) => {
      const success = await moderation.handleUpdateThreadTags(threadId, tagIds);
      if (success) handleCloseEditTags();
    },
    [moderation, handleCloseEditTags],
  );

  const handleOpenAnnouncement = useCallback(() => {
    setAnnouncementVisible(true);
  }, []);

  const handleCloseAnnouncement = useCallback(() => {
    setAnnouncementVisible(false);
  }, []);

  const handleSaveAnnouncement = useCallback(
    async (data: { title: string; body: string; tagIds: number[] }) => {
      const success = await moderation.handleCreateAnnouncement(data);
      if (success) handleCloseAnnouncement();
    },
    [moderation, handleCloseAnnouncement],
  );

  const handleDelete = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      const confirmed = await showConfirm(
        'Delete Thread',
        `Are you sure you want to delete "${thread.title}"? This action cannot be undone.`,
        'Delete',
        'Cancel',
      );
      if (confirmed) {
        await moderation.handleDeleteThread(thread.id);
      }
    },
    [moderation],
  );

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="ThreadModerationDesktop">
        <DesktopThreadModerationView
          moderation={moderation}
          editTagsVisible={editTagsVisible}
          editingThread={editingThread}
          announcementVisible={announcementVisible}
          onOpenEditTags={handleOpenEditTags}
          onCloseEditTags={handleCloseEditTags}
          onSaveTags={handleSaveTags}
          onOpenAnnouncement={handleOpenAnnouncement}
          onCloseAnnouncement={handleCloseAnnouncement}
          onSaveAnnouncement={handleSaveAnnouncement}
          onDelete={handleDelete}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="ThreadModeration">
      <BaseAdminScreen
        screenName="ThreadModeration"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Threads"
          emptyStateType="corkBoardThreads"
          onAddPress={handleOpenAnnouncement}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true}
        >
          <View style={styles.searchContainer}>
            <TextInput
              style={styles.searchInput}
              value={moderation.searchInput}
              onChangeText={moderation.setSearchInput}
              placeholder="Search threads..."
              placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
            />
          </View>
          <AdminSelectDropdown
            label="Sort"
            options={CORK_BOARD_SORT_OPTIONS}
            selectedValue={moderation.sortBy}
            onSelect={(value) => moderation.setSortBy(value as ThreadSortBy)}
          />

          {moderation.error && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorBannerText}>Failed to load threads.</Text>
              <TouchableOpacity onPress={moderation.refetch}>
                <Text style={styles.errorRetryText}>Retry</Text>
              </TouchableOpacity>
            </View>
          )}

          <ThreadModerationList
            threads={moderation.threads}
            isLoading={moderation.isLoading}
            isMutating={moderation.isMutating}
            onPin={moderation.handlePinThread}
            onLock={moderation.handleLockThread}
            onEditTags={handleOpenEditTags}
            onDelete={handleDelete}
            onRefresh={moderation.refetch}
            isRefreshing={moderation.isFetching}
            onLoadMore={moderation.loadMore}
            hasNextPage={moderation.hasNextPage}
            isFetchingMore={moderation.isFetchingNextPage}
          />
        </AdminEmptyScreenLayout>

        {/* Modals */}
        <EditThreadTagsModal
          visible={editTagsVisible}
          thread={editingThread}
          availableTags={moderation.tags}
          isSaving={moderation.isUpdatingTags}
          onSave={handleSaveTags}
          onClose={handleCloseEditTags}
        />
        <AnnouncementFormModal
          visible={announcementVisible}
          availableTags={moderation.tags}
          isSaving={moderation.isCreatingAnnouncement}
          onSave={handleSaveAnnouncement}
          onClose={handleCloseAnnouncement}
        />

      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  searchContainer: {
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  searchInput: {
    backgroundColor: ADMIN_COLORS.INPUT.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.INPUT.BORDER,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    backgroundColor: '#FEF2F2',
    borderBottomWidth: 1,
    borderBottomColor: '#FECACA',
  },
  errorBannerText: {
    fontSize: 13,
    color: ADMIN_COLORS.ERROR,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    textDecorationLine: 'underline',
  },
});
